<!-- src/views/ContractList.vue -->
<template>
  <div class="contract-list-container">
    <!-- 搜索区域 -->
    <el-card class="search-card" shadow="never">
      <el-form :model="queryParams" :inline="true" class="search-form">
        <el-form-item label="合同编号">
          <el-input
            v-model="queryParams.contractNo"
            placeholder="请输入合同编号"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        
        <el-form-item label="合同主体">
          <el-input
            v-model="queryParams.contractSubject"
            placeholder="请输入合同主体"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        
        <el-form-item label="租赁楼宇">
          <el-input
            v-model="queryParams.leaseBuilding"
            placeholder="请输入租赁楼宇"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        
        <el-form-item label="签约时间">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            style="width: 320px"
            @change="handleDateChange"
          />
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">
            搜索
          </el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 操作按钮和统计信息 -->
    <div class="header-area">
      <div class="operation-bar">
        <el-button 
          type="primary" 
          icon="el-icon-plus" 
          @click="handleAdd"
        >
          新增合同
        </el-button>
        <el-button 
          icon="el-icon-download" 
          @click="handleExport"
        >
          导出数据
        </el-button>
        <el-button 
          icon="el-icon-refresh" 
          @click="refreshData"
        >
          刷新
        </el-button>
      </div>
      
      <div class="statistics">
        <span class="stat-item">
          共 <span class="highlight">{{ total }}</span> 条记录
        </span>
        <span class="stat-item">
          总租赁面积: <span class="highlight">{{ formatNumber(totalLeaseArea) }}</span> ㎡
        </span>
        <span class="stat-item">
          总首年租金: <span class="highlight">{{ formatCurrency(totalFirstYearRent) }}</span> 元
        </span>
      </div>
    </div>

    <!-- 数据表格 -->
    <el-card class="table-card" shadow="never">
      <el-table
        ref="contractTable"
        v-loading="loading"
        :data="tableData"
        border
        stripe
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa', color: '#333', fontWeight: '600' }"
        @sort-change="handleSortChange"
        @selection-change="handleSelectionChange"
      >
        <!-- 选择列 -->
        <el-table-column
          type="selection"
          width="55"
          align="center"
        />
        
        <!-- 合同编号 -->
        <el-table-column 
          prop="contractNo" 
          label="合同编号" 
          width="150"
          show-overflow-tooltip
          sortable="custom"
          fixed="left"
        />
        
        <!-- 合同主体 -->
        <el-table-column 
          prop="contractSubject" 
          label="合同主体" 
          width="180"
          show-overflow-tooltip
        />
        
        <!-- 开票信息 -->
        <el-table-column 
          prop="invoiceInfo" 
          label="开票信息" 
          width="200"
          show-overflow-tooltip
        />
        
        <!-- 签约开始日期 -->
        <el-table-column 
          prop="signStartDate" 
          label="签约开始日期" 
          width="130"
          sortable="custom"
          align="center"
        >
          <template slot-scope="{ row }">
            <span :class="getDateClass(row.signStartDate)">
              {{ row.signStartDate }}
            </span>
          </template>
        </el-table-column>
        
        <!-- 签约结束日期 -->
        <el-table-column 
          prop="signEndDate" 
          label="签约结束日期" 
          width="130"
          sortable="custom"
          align="center"
        >
          <template slot-scope="{ row }">
            <span :class="getDateClass(row.signEndDate)">
              {{ row.signEndDate }}
            </span>
          </template>
        </el-table-column>
        
        <!-- 租赁楼宇 -->
        <el-table-column 
          prop="leaseBuilding" 
          label="租赁楼宇" 
          width="150"
        />
        
        <!-- 租赁位置 -->
        <el-table-column 
          prop="leaseLocation" 
          label="租赁位置" 
          width="180"
          show-overflow-tooltip
        />
        
        <!-- 租赁面积 -->
        <el-table-column 
          prop="leaseArea" 
          label="租赁面积(㎡)" 
          width="130"
          align="right"
          sortable="custom"
        >
          <template slot-scope="{ row }">
            <span class="number-cell">
              {{ formatNumber(row.leaseArea) }}
            </span>
          </template>
        </el-table-column>
        
        <!-- 首年租金单价 -->
        <el-table-column 
          prop="firstYearRentUnitPrice" 
          label="租金单价(元/㎡/月)" 
          width="150"
          align="right"
        >
          <template slot-scope="{ row }">
            <span class="number-cell">
              {{ formatNumber(row.firstYearRentUnitPrice) }}
            </span>
          </template>
        </el-table-column>
        
        <!-- 租金政策 -->
        <el-table-column 
          prop="rentPolicy" 
          label="租金政策" 
          width="200"
          show-overflow-tooltip
        />
        
        <!-- 租金支付方式 -->
        <el-table-column 
          prop="rentPaymentMethod" 
          label="支付方式" 
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-tag 
              :type="getPaymentMethodTagType(row.rentPaymentMethod)"
              size="small"
            >
              {{ row.rentPaymentMethod || '未设置' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <!-- 首年租金 -->
        <el-table-column 
          prop="firstYearRent" 
          label="首年租金(元)" 
          width="150"
          align="right"
          sortable="custom"
        >
          <template slot-scope="{ row }">
            <span class="money-cell">
              {{ formatCurrency(row.firstYearRent) }}
            </span>
          </template>
        </el-table-column>
        
        <!-- 押金金额 -->
        <el-table-column 
          prop="depositAmount" 
          label="押金金额(元)" 
          width="150"
          align="right"
        >
          <template slot-scope="{ row }">
            <span class="money-cell">
              {{ formatCurrency(row.depositAmount) }}
            </span>
          </template>
        </el-table-column>
        
        <!-- 备注 -->
        <el-table-column 
          prop="remark" 
          label="备注" 
          width="200"
          show-overflow-tooltip
        />
        
        <!-- 创建时间 -->
        <el-table-column 
          prop="createTime" 
          label="创建时间" 
          width="160"
          sortable="custom"
        >
          <template slot-scope="{ row }">
            {{ formatDateTime(row.createTime) }}
          </template>
        </el-table-column>
        
        <!-- 更新时间 -->
        <el-table-column 
          prop="updateTime" 
          label="更新时间" 
          width="160"
          sortable="custom"
        >
          <template slot-scope="{ row }">
            {{ formatDateTime(row.updateTime) }}
          </template>
        </el-table-column>
        
        <!-- 操作列 -->
        <el-table-column 
          label="操作" 
          width="200" 
          fixed="right"
          align="center"
        >
          <template slot-scope="{ row }">
            <div class="action-buttons">
              <el-tooltip content="查看详情" placement="top">
                <el-button
                  type="text"
                  size="small"
                  icon="el-icon-view"
                  @click="handleView(row)"
                />
              </el-tooltip>
              
              <el-tooltip content="编辑" placement="top">
                <el-button
                  type="text"
                  size="small"
                  icon="el-icon-edit"
                  @click="handleEdit(row)"
                />
              </el-tooltip>
              
              <el-tooltip content="删除" placement="top">
                <el-button
                  type="text"
                  size="small"
                  icon="el-icon-delete"
                  style="color: #f56c6c"
                  @click="handleDelete(row)"
                />
              </el-tooltip>
              
              <el-tooltip content="复制" placement="top">
                <el-button
                  type="text"
                  size="small"
                  icon="el-icon-document-copy"
                  @click="handleCopy(row)"
                />
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="footer-area">
        <div class="batch-operations" v-if="selectedRows.length > 0">
          <span class="selected-count">已选择 {{ selectedRows.length }} 项</span>
          <el-button 
            type="text" 
            size="small"
            @click="batchDelete"
          >
            批量删除
          </el-button>
          <el-button 
            type="text" 
            size="small"
            @click="batchExport"
          >
            批量导出
          </el-button>
          <el-button 
            type="text" 
            size="small"
            @click="clearSelection"
          >
            取消选择
          </el-button>
        </div>
        
        <el-pagination
          v-if="total > 0"
          class="pagination-container"
          :current-page="queryParams.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="queryParams.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 合同表单对话框 -->
    <contract-form
      ref="contractForm"
      :visible="dialogVisible"
      :form-data="currentFormData"
      :is-edit="isEditMode"
      @close="handleDialogClose"
      @save="handleFormSave"
    />
    <contract-detail-dialog
      :visible="detailDialogVisible"
      :contract-data="currentDetailData"
      @close="handleDetailDialogClose"
    />
  </div>
</template>

<script>
import ContractForm from '../components/ContractForm.vue'
import contractApi from '../api/contract'
import ContractDetailDialog from '../components/ContractDetailDialog.vue'  // 导入详情对话框组件

export default {
  name: 'ContractList',
  components: { ContractForm, ContractDetailDialog},
  
  
  data() {
    return {
      // 加载状态
      loading: false,
      
      // 表格数据
      tableData: [],
      total: 0,
      totalLeaseArea: 0,
      totalFirstYearRent: 0,
      
      // 选择的行
      selectedRows: [],
      
      // 查询参数
      queryParams: {
        contractNo: '',
        contractSubject: '',
        leaseBuilding: '',
        signStartDateFrom: '',
        signStartDateTo: '',
        pageNum: 1,
        pageSize: 10,
        sortField: '',
        sortOrder: ''
      },
      //// 详情对话框相关
      detailDialogVisible: false,
      currentDetailData: {},

      // 日期范围
      dateRange: [],
      
      // 对话框相关
      dialogVisible: false,
      isEditMode: false,
      currentFormData: {},
      
      // 自动刷新定时器
      refreshTimer: null
    }
  },
  
  computed: {
    // 计算统计数据
    stats() {
      let totalArea = 0
      let totalRent = 0
      
      this.tableData.forEach(item => {
        if (item.leaseArea) {
          totalArea += Number(item.leaseArea)
        }
        if (item.firstYearRent) {
          totalRent += Number(item.firstYearRent)
        }
      })
      
      return {
        totalArea,
        totalRent
      }
    }
  },
  
  watch: {
    // 监听数据变化，更新统计
    tableData: {
      handler() {
        this.updateStatistics()
      },
      deep: true
    }
  },
  
  created() {
    // 页面创建时立即加载数据
    this.loadData()
    
    // 设置自动刷新（每5分钟刷新一次）
    this.setupAutoRefresh()
  },
  
  beforeDestroy() {
    // 清除定时器
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
  },
  
  methods: {
    /**
     * 加载数据
     */
/**
 * 加载数据 - 优化版本
 */
    async loadData() {
      this.loading = true
      try {
        console.log('开始加载合同数据...')
        
        const response = await contractApi.getAllContracts()
        console.log('API 返回原始数据：', response)
        
        // 检查响应是否有效
        if (response && response.data) {
          const responseData = response.data
          
          if (responseData.code === 20000 && responseData.flag === true) {
            // 获取数据列表
            const dataList = responseData.data || []
            
            console.log(`成功加载 ${dataList.length} 条数据`)
            
            // 更新表格数据
            this.tableData = dataList
            this.total = dataList.length
            
            // 更新统计数据
            this.updateStatistics()
            
            // 显示提示信息
            if (dataList.length === 0) {
              this.$message.info('暂无合同数据')
            } else {
              this.$message.success(`已加载 ${dataList.length} 条合同数据`)
            }
          } else {
            // API返回失败
            const errorMessage = responseData.message || responseData.msg || '加载失败'
            this.$message.error(errorMessage)
            
            // 清空数据
            this.tableData = []
            this.total = 0
          }
        } else {
          // 响应格式不正确
          this.$message.error('加载失败，响应格式不正确')
          this.tableData = []
          this.total = 0
        }
      } catch (error) {
        console.error('加载合同数据失败:', error)
        
        // 详细错误信息
        if (error.response) {
          console.error('错误响应:', error.response)
          console.error('状态码:', error.response.status)
          console.error('错误数据:', error.response.data)
          
          if (error.response.status === 401) {
            this.$message.error('登录已过期，请重新登录')
            // 可以在这里跳转到登录页
            // this.$router.push('/login')
          }
        }
        
        // 错误处理
        if (process.env.NODE_ENV === 'development') {
          console.log('使用模拟数据进行开发测试...')
          this.useMockData()
        } else {
          this.$message.error('加载失败，请检查网络连接')
          this.tableData = []
          this.total = 0
        }
      } finally {
        this.loading = false
      }
    },
// 添加模拟数据方法
  useMockData() {
    const mockData = [
      {
        id: 1,
        contractNo: 'HT20240001',
        contractSubject: '示例科技有限公司',
        invoiceInfo: '增值税普通发票，税号：91330101MA2XXXXXXX',
        signStartDate: '2024-01-01',
        signEndDate: '2024-12-31',
        leaseBuilding: 'A栋商务楼',
        leaseLocation: '3层301-305室',
        leaseArea: 150.50,
        firstYearRentUnitPrice: 85.00,
        rentPolicy: '首年租金不变，第二年递增5%',
        rentPaymentMethod: '季付',
        firstYearRent: 153510.00,
        depositAmount: 30000.00,
        remark: '重要客户，需特别关注',
        createTime: '2024-01-01 10:30:00',
        updateTime: '2024-01-01 10:30:00'
      },
      {
        id: 2,
        contractNo: 'HT20240002',
        contractSubject: '测试有限公司',
        invoiceInfo: '增值税专用发票',
        signStartDate: '2024-02-01',
        signEndDate: '2025-01-31',
        leaseBuilding: 'B栋写字楼',
        leaseLocation: '5层501室',
        leaseArea: 80.00,
        firstYearRentUnitPrice: 75.00,
        rentPolicy: '按年递增3%',
        rentPaymentMethod: '月付',
        firstYearRent: 72000.00,
        depositAmount: 12000.00,
        remark: '',
        createTime: '2024-02-01 14:20:00',
        updateTime: '2024-02-01 14:20:00'
      }
    ]
    
    this.tableData = mockData
    this.total = mockData.length
    this.updateStatistics()
    this.$message.success('已加载模拟数据')
  },
      
    /**
     * 更新统计数据
     */
    updateStatistics() {
      let totalArea = 0
      let totalRent = 0
      
      this.tableData.forEach(item => {
        if (item.leaseArea) {
          totalArea += Number(item.leaseArea)
        }
        if (item.firstYearRent) {
          totalRent += Number(item.firstYearRent)
        }
      })
      
      this.totalLeaseArea = totalArea
      this.totalFirstYearRent = totalRent
    },
    

    
    /**
     * 设置自动刷新
     */
    setupAutoRefresh() {
      // 每5分钟自动刷新一次数据
      this.refreshTimer = setInterval(() => {
        console.log('自动刷新合同数据...')
        this.loadData()
      }, 5 * 60 * 1000) // 5分钟
    },
    
    /**
 * 手动刷新数据 - 强制刷新
 */
refreshData() {
  console.log('手动刷新数据...')
  
  // 重置分页到第一页
  this.queryParams.pageNum = 1
  
  // 清除选择
  this.clearSelection()
  
  // 重新加载数据
  this.loadData()
  
  this.$message.success('正在刷新数据...')
},
    
    /**
     * 立即从表格中移除已删除的项目
     */
    removeDeletedItemFromTable(id) {
      const index = this.tableData.findIndex(item => item.contractid === id)
      if (index !== -1) {
        // 从表格数据中移除
        this.tableData.splice(index, 1)
        
        // 更新总数
        this.total = this.tableData.length
        
        // 更新统计数据
        this.updateStatistics()
        
        console.log(`已从表格中移除 ID 为 ${id} 的项目`)
        return true
      }
      return false
    },

    /**
     * 立即从表格中移除多个项目
     */
    removeDeletedItemsFromTable(ids) {
      const originalLength = this.tableData.length
      this.tableData = this.tableData.filter(item => !ids.includes(item.contractid))
      const removedCount = originalLength - this.tableData.length
      
      if (removedCount > 0) {
        this.total = this.tableData.length
        this.updateStatistics()
        console.log(`已从表格中移除 ${removedCount} 个项目`)
      }
      
      return removedCount
    },
    /**
     * 处理搜索
     */
    handleSearch() {
      this.queryParams.pageNum = 1
      this.loadData()
    },
    
    /**
     * 处理日期范围变化
     */
    handleDateChange(dateRange) {
      if (dateRange && dateRange.length === 2) {
        this.queryParams.signStartDateFrom = dateRange[0]
        this.queryParams.signStartDateTo = dateRange[1]
      } else {
        this.queryParams.signStartDateFrom = ''
        this.queryParams.signStartDateTo = ''
      }
    },
    
    /**
     * 重置搜索条件
     */
    handleReset() {
      this.queryParams = {
        contractNo: '',
        contractSubject: '',
        leaseBuilding: '',
        signStartDateFrom: '',
        signStartDateTo: '',
        pageNum: 1,
        pageSize: 10,
        sortField: '',
        sortOrder: ''
      }
      this.dateRange = []
      this.loadData()
    },
    
    /**
     * 处理排序
     */
    handleSortChange({ column, prop, order }) {
      this.queryParams.sortField = prop
      this.queryParams.sortOrder = order === 'ascending' ? 'asc' : 
                                  order === 'descending' ? 'desc' : ''
      this.loadData()
    },
    
    /**
     * 处理选择变化
     */
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },
    
    /**
     * 处理新增
     */
    handleAdd() {
      this.currentFormData = {
        contractNo: '',
        contractSubject: '',
        invoiceInfo: '',
        signStartDate: '',
        signEndDate: '',
        leaseBuilding: '',
        leaseLocation: '',
        leaseArea: null,
        firstYearRentUnitPrice: null,
        rentPolicy: '',
        rentPaymentMethod: '',
        firstYearRent: null,
        depositAmount: null,
        remark: ''
      }
      this.isEditMode = false
      this.dialogVisible = true
    },
    
    /**
     * 处理编辑
     */
    handleEdit(row) {
      this.currentFormData = { ...row }
      this.isEditMode = true
      this.dialogVisible = true
    },
    
    /**
     * 处理查看
     */
    handleView(row) {
      this.currentDetailData = { ...row }
      this.detailDialogVisible = true
    },
    
    /**
     * 处理删除
     */
    handleDetailDialogClose() {
      this.detailDialogVisible = false
      this.currentDetailData = {}
    },
    /**
 * 处理删除
 */
    handleDelete(row) {
      this.$confirm(`确定要删除合同 "${row.contractNo}" 吗？`, '提示', {
        type: 'warning',
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        beforeClose: (action, instance, done) => {
          if (action === 'confirm') {
            instance.confirmButtonLoading = true
            this.performDelete(row.contractid, row.contractNo).finally(() => {
              instance.confirmButtonLoading = false
              done()
            })
          } else {
            done()
          }
        }
      }).catch(() => {
        // 用户取消删除
      })
    },

    /**
     * 执行删除操作 - 优化版本（即时更新）
     */
    async performDelete(id, contractNo) {
      try {
        console.log('开始删除合同，ID:', id, '编号:', contractNo)
        
        // 先备份当前数据，以便回滚
        const originalData = [...this.tableData]
        
        // 先立即从表格中移除（优化用户体验）
        const removed = this.removeDeletedItemFromTable(id)
        
        const response = await contractApi.deleteContract(id)
        console.log('删除响应:', response)
        
        // 检查响应
        let success = false
        let message = ''
        
        if (response && response.data) {
          const responseData = response.data
          
          if (responseData.flag === true || responseData.success === true) {
            success = true
            message = responseData.message || `合同 "${contractNo}" 删除成功`
          } else {
            message = responseData.message || '删除失败'
          }
        }
        
        if (success) {
          this.$message.success(message)
          
          // 清除选择
          this.clearSelection()
          
          // 可以再重新加载一次数据，确保数据完全同步
          setTimeout(() => {
            this.loadData()
          }, 500)
          
        } else {
          // 删除失败，恢复数据
          this.tableData = originalData
          this.total = this.tableData.length
          this.updateStatistics()
          
          this.$message.error(message || '删除失败')
        }
        
      } catch (error) {
        console.error('删除失败:', error)
        
        // 删除失败，恢复数据
        if (originalData) {
          this.tableData = originalData
          this.total = this.tableData.length
          this.updateStatistics()
        }
        
        // 错误处理
        let errorMsg = '删除失败'
        if (error.response && error.response.data && error.response.data.message) {
          errorMsg = error.response.data.message
        } else if (error.message) {
          errorMsg = error.message
        }
        
        this.$message.error(errorMsg)
      }
    },
    /**
     * 处理删除成功
     */
    handleDeleteSuccess(deletedId, contractNo) {
      console.log(`合同 ${contractNo} 删除成功，ID: ${deletedId}`)
      
      // 方法1：立即从表格中移除
      this.removeDeletedItemFromTable(deletedId)
      
      // 方法2：重新加载数据（更可靠）
      this.loadData()
      
      // 清除选择
      this.clearSelection()
      
      this.$message.success(`合同 "${contractNo}" 已删除`)
    },
    /**
     * 处理复制
     */
    handleCopy(row) {
      // 复制合同数据，生成新的合同编号
      const newContractNo = `COPY_${row.contractNo}_${Date.now()}`
      this.currentFormData = {
        ...row,
        contractid: null,
        contractNo: newContractNo,
        createTime: '',
        updateTime: ''
      }
      this.isEditMode = false
      this.dialogVisible = true
    },
    
    /**
     * 批量删除
     */
    batchDelete() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先选择要删除的合同')
        return
      }
      
      const contractNos = this.selectedRows.map(row => row.contractNo).join(', ')
      this.$confirm(`确定要删除选中的 ${this.selectedRows.length} 个合同吗？`, '提示', {
        type: 'warning',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }).then(async () => {
        this.loading = true
        
        try {
          // 记录要删除的ID，用于后续从表格中移除
          const deleteIds = this.selectedRows.map(row => row.contractid)
          const deleteNos = this.selectedRows.map(row => row.contractNo)
          
          console.log('批量删除IDs:', deleteIds)
          console.log('批量删除合同编号:', deleteNos)
          
          // 方法1：逐个删除（更可靠）
          let successCount = 0
          let failCount = 0
          
          for (const row of this.selectedRows) {
            try {
              const response = await contractApi.deleteContract(row.contractid)
              
              if (response && response.data && response.data.flag === true) {
                successCount++
              } else {
                failCount++
                const errorMessage = response && response.data && response.data.message || ''
                console.error(`删除 ${row.contractNo} 失败:`, errorMessage)
              }
            } catch (error) {
              failCount++
              console.error(`删除 ${row.contractNo} 失败:`, error.message)
            }
          }
          
          if (successCount > 0) {
            this.$message.success(`成功删除 ${successCount} 个合同`)
            
            // 重新加载数据
            this.loadData()
            
            // 或者从当前表格中移除已删除的行
            this.tableData = this.tableData.filter(item => !deleteIds.includes(item.contractid))
            this.total = this.tableData.length
            this.updateStatistics()
          }
          
          if (failCount > 0) {
            this.$message.warning(`${failCount} 个合同删除失败`)
          }
          
          // 清除选择
          this.clearSelection()
          
        } catch (error) {
          console.error('批量删除失败:', error)
          this.$message.error('批量删除失败: ' + (error.message || '未知错误'))
        } finally {
          this.loading = false
        }
      }).catch(() => {
        // 用户取消
      })
    },
    /**
     * 批量导出
     */
    batchExport() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先选择要导出的合同')
        return
      }
      
      // TODO: 实现批量导出功能
      this.$message.info(`准备导出 ${this.selectedRows.length} 个合同数据...`)
    },
    
    /**
     * 清除选择
     */
    clearSelection() {
      if (this.$refs.contractTable) {
        this.$refs.contractTable.clearSelection()
      }
      this.selectedRows = []
    },
    
    /**
     * 处理对话框关闭
     */
    handleDialogClose() {
      this.dialogVisible = false
      this.currentFormData = {}
    },
    
    /**
     * 处理分页大小变化
     */
    handleSizeChange(size) {
      this.queryParams.pageSize = size
      this.queryParams.pageNum = 1
      this.loadData()
    },
    
    /**
     * 处理页码变化
     */
    handleCurrentChange(page) {
      this.queryParams.pageNum = page
      this.loadData()
    },
    
    /**
     * 处理数据导出
     */
    handleExport() {
      // TODO: 实现导出功能
      this.$message.info('导出功能正在开发中...')
    },
    
    /**
     * 获取支付方式标签类型
     */
    getPaymentMethodTagType(method) {
      const map = {
        '月付': 'primary',
        '季付': 'success',
        '半年付': 'warning',
        '年付': 'danger',
        '一次性支付': 'info'
      }
      return map[method] || 'info'
    },
    
    /**
     * 获取日期样式类
     */
    getDateClass(dateStr) {
      const date = new Date(dateStr)
      const now = new Date()
      const oneMonthLater = new Date()
      oneMonthLater.setMonth(oneMonthLater.getMonth() + 1)
      
      if (date < now) {
        return 'expired-date'
      } else if (date <= oneMonthLater) {
        return 'expiring-date'
      }
      return ''
    },
    
    /**
     * 格式化数字
     */
    formatNumber(value) {
      if (value == null || value === '') return '-'
      const num = Number(value)
      if (isNaN(num)) return '-'
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    /**
     * 格式化货币
     */
    formatCurrency(value) {
      if (value == null || value === '') return '-'
      const num = Number(value)
      if (isNaN(num)) return '-'
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    /**
     * 格式化日期时间
     */
    formatDateTime(value) {
      if (!value) return '-'
      return value
    },
    /**
 * 处理新增
 */
    handleAdd() {
      // 清空表单数据
      this.currentFormData = {}
      this.isEditMode = false
      this.dialogVisible = true
    },

    /**
     * 处理编辑
     */
    handleEdit(row) {
      // 深拷贝数据，避免直接修改
      this.currentFormData = JSON.parse(JSON.stringify(row))
      this.isEditMode = true
      this.dialogVisible = true
    },
/**
 * 处理表单保存
 */
    async handleFormSave(formData) {
      try {
        console.log('保存的数据:', formData)
        console.log('编辑模式:', this.isEditMode)
        
        let response
        let success = false
        let message = ''
        
        if (this.isEditMode) {
          // 编辑模式 - 确保有合同ID
          if (!formData.contractid) {
            this.$message.error('合同ID不存在，无法更新')
            this.dialogVisible = false
            return
          }
          
          // 检查数据是否真的有更改
          const originalData = this.tableData.find(item => item.contractid === formData.contractid)
          if (originalData && !this.hasDataChanged(originalData, formData)) {
            this.$message.info('数据未发生变化，无需保存')
            this.dialogVisible = false
            return
          }
          
          // 编辑模式
          response = await contractApi.updateContract(formData)
          
          // 解析响应 - 根据您的API结构
          if (response && response.data) {
            const responseData = response.data
            success = responseData.flag === true || responseData.success === true
            message = responseData.message || '更新成功'
          }
        } else {
          // 新增模式
          response = await contractApi.addContract(formData)
          
          // 解析响应 - 根据您的API结构
          if (response && response.data) {
            const responseData = response.data
            success = responseData.flag === true || responseData.success === true
            message = responseData.message || '新增成功'
          }
        }
        
        
        if (success) {
          this.$message.success(message)
          this.dialogVisible = false
          // 重新加载数据
          this.loadData()
        } else {
          // 从响应中获取错误信息
          let errorMsg = '保存失败'
          if (response && response.data) {
            const responseData = response.data
            errorMsg = responseData.message || responseData.msg || errorMsg
          } else if (response && response.message) {
            errorMsg = response.message
          }
          this.$message.error(errorMsg)
        }
      } catch (error) {
        console.error('保存失败:', error)
        
        // 更详细的错误信息
        let errorMsg = '保存失败'
        
        if (error.response) {
          console.error('响应状态:', error.response.status)
          console.error('响应数据:', error.response.data)
          
          // 从响应数据中获取错误信息
          if (error.response.data) {
            const responseData = error.response.data
            if (responseData.message) {
              errorMsg = responseData.message
            } else if (responseData.msg) {
              errorMsg = responseData.msg
            } else if (typeof responseData === 'string') {
              errorMsg = responseData
            }
          }
        } else if (error.message) {
          errorMsg = error.message
        }
        
        this.$message.error(errorMsg)
      } finally {
        this.dialogVisible = false
      }
    },
    hasDataChanged(original, modified) {
      // 需要比较的字段列表
      const fieldsToCompare = [
        'contractNo',
        'contractSubject',
        'invoiceInfo',
        'signStartDate',
        'signEndDate',
        'leaseBuilding',
        'leaseLocation',
        'leaseArea',
        'firstYearRentUnitPrice',
        'rentPolicy',
        'rentPaymentMethod',
        'firstYearRent',
        'depositAmount',
        'remark'
      ]
      
      for (const field of fieldsToCompare) {
        const originalValue = original[field]
        const modifiedValue = modified[field]
        
        // 处理 null 和 undefined
        if ((originalValue === null || originalValue === undefined) && 
            (modifiedValue === null || modifiedValue === undefined)) {
          continue
        }
        
        // 处理数字类型
        if (typeof originalValue === 'number' || typeof modifiedValue === 'number') {
          if (Number(originalValue) !== Number(modifiedValue)) {
            console.log(`字段 ${field} 有变化:`, originalValue, '->', modifiedValue)
            return true
          }
          continue
        }
        
        // 处理字符串类型
        if (String(originalValue) !== String(modifiedValue)) {
          console.log(`字段 ${field} 有变化:`, originalValue, '->', modifiedValue)
          return true
        }
      }
      
      return false
    },

    /**
     * 处理对话框关闭
     */
    handleDialogClose() {
      this.dialogVisible = false
      this.currentFormData = {}
    },

  }
}
</script>

<style scoped>
.contract-list-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(90vh - 84px);
}

.search-card {
  margin-bottom: 20px;
  border-radius: 4px;
}

.search-form {
  margin-bottom: -20px;
}

.header-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.operation-bar .el-button {
  margin-right: 10px;
}

.statistics {
  display: flex;
  gap: 30px;
}

.stat-item {
  color: #606266;
  font-size: 14px;
}

.stat-item .highlight {
  color: #409EFF;
  font-weight: 600;
  margin: 0 4px;
}

.table-card {
  border-radius: 4px;
}

.footer-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.batch-operations {
  display: flex;
  align-items: center;
  gap: 16px;
}

.selected-count {
  color: #409EFF;
  font-size: 14px;
}

.pagination-container {
  text-align: right;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.action-buttons .el-button {
  padding: 6px;
}

.el-table {
  margin-top: 10px;
}

/* 数字单元格样式 */
.number-cell {
  font-family: 'Arial', sans-serif;
  font-weight: 500;
  color: #303133;
}

.money-cell {
  font-family: 'Arial', sans-serif;
  font-weight: 600;
  color: #f56c6c;
}

/* 日期样式 */
.expired-date {
  color: #f56c6c;
}

.expiring-date {
  color: #e6a23c;
}

/* 表格行样式 */
.el-table .warning-row {
  background-color: #fdf6ec;
}

.el-table .expired-row {
  background-color: #fef0f0;
}

.el-table .success-row {
  background-color: #f0f9eb;
}
</style>

<style>
/* 全局样式 */
.el-table th > .cell {
  white-space: nowrap;
}

.el-table .el-tag {
  min-width: 60px;
}

/* 对话框样式 */
.el-dialog__body {
  max-height: 70vh;
  overflow-y: auto;
}
</style>